import React, { useMemo, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import CategoryHead from '../components/CategoryHead';
import BooksGrid from '../components/BooksGrid';

interface Book {
  book_id: number;
  book_title: string;
  author_id: number;
  book_description?: string;
  book_price?: number;
  book_cover_path?: string;
  book_genres?: string[];
  book_quantity?: number;
  book_page_count?: number;
  book_publication_year?: number;
  publisher_id?: number;
  author_name?: string;
  publisher_name?: string;
}

interface Author {
  author_id: number;
  author_name: string;
}

interface Publisher {
  publisher_id: number;
  name: string;
}

interface Genre {
  genre_id: number;
  genre_name: string;
}

interface Language {
  language_id: number;
  language_name: string;
}

const CatalogContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: #f8f9fa;
`;

const ContentContainer = styled.main`
  width: 100%;
  max-width: 1200px;
  min-height: 100vh;
  padding: 20px;
  background-color: white;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
`;

const TopSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 30px;
  gap: 20px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
  font-family: "IBM Plex Mono", monospace;
  font-size: 18px;
  color: #a13b16;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  font-family: "IBM Plex Mono", monospace;
  color: #6c757d;
`;

const EmptyStateTitle = styled.h3`
  font-size: 24px;
  color: #a13b16;
  margin-bottom: 10px;
`;

const EmptyStateText = styled.p`
  font-size: 16px;
  margin-bottom: 20px;
  line-height: 1.5;
`;

const Catalog: React.FC = React.memo(() => {
  const location = useLocation();
  const [books, setBooks] = useState<Book[]>([]);
  const [authors, setAuthors] = useState<Author[]>([]);
  const [publishers, setPublishers] = useState<Publisher[]>([]);
  const [genres, setGenres] = useState<Genre[]>([]);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const queryParams = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      term: params.get('term'),
      genre: params.get('genre'),
      author: params.get('author'),
      publisher: params.get('publisher'),
      language: params.get('language')
    };
  }, [location.search]);

  const queryInfo = useMemo(() => {
    if (queryParams.term) {
      return { type: 'search', value: queryParams.term, label: `Пошук: "${queryParams.term}"` };
    }
    if (queryParams.genre) {
      const genre = genres.find(g => g.genre_id.toString() === queryParams.genre);
      return { type: 'genre', value: queryParams.genre, label: `Жанр: ${genre?.genre_name || queryParams.genre}` };
    }
    if (queryParams.author) {
      const author = authors.find(a => a.author_id.toString() === queryParams.author);
      return { type: 'author', value: queryParams.author, label: `Автор: ${author?.author_name || queryParams.author}` };
    }
    if (queryParams.publisher) {
      const publisher = publishers.find(p => p.publisher_id.toString() === queryParams.publisher);
      return { type: 'publisher', value: queryParams.publisher, label: `Видавництво: ${publisher?.name || queryParams.publisher}` };
    }
    if (queryParams.language) {
      const language = languages.find(l => l.language_id.toString() === queryParams.language);
      return { type: 'language', value: queryParams.language, label: `Мова: ${language?.language_name || queryParams.language}` };
    }
    return { type: 'all', value: null, label: 'Всі книги' };
  }, [queryParams, authors, publishers, genres, languages]);

  useEffect(() => {
    const fetchMetaData = async () => {
      try {
        const [authorsRes, publishersRes, genresRes, languagesRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_BASE_API_URL}/authors`),
          fetch(`${process.env.REACT_APP_BASE_API_URL}/publishers`),
          fetch(`${process.env.REACT_APP_BASE_API_URL}/genres`),
          fetch(`${process.env.REACT_APP_BASE_API_URL}/languages`)
        ]);

        const [authorsData, publishersData, genresData, languagesData] = await Promise.all([
          authorsRes.json(),
          publishersRes.json(),
          genresRes.json(),
          languagesRes.json()
        ]);

        setAuthors(authorsData);
        setPublishers(publishersData);
        setGenres(genresData);
        setLanguages(languagesData);
      } catch (err) {
        console.error('Помилка завантаження метаданих:', err);
      }
    };

    fetchMetaData();
  }, []);

  useEffect(() => {
    const fetchBooks = async () => {
      try {
        setLoading(true);
        let url = `${process.env.REACT_APP_BASE_API_URL}/books`;

        if (queryParams.term) {
          url += `/search?q=${encodeURIComponent(queryParams.term)}`;
        } else if (queryParams.genre) {
          url += `/genre/${encodeURIComponent(queryParams.genre)}`;
        } else if (queryParams.author) {
          url += `/author/${encodeURIComponent(queryParams.author)}`;
        } else if (queryParams.publisher) {
          url += `/publisher/${encodeURIComponent(queryParams.publisher)}`;
        } else if (queryParams.language) {
          url += `/language/${encodeURIComponent(queryParams.language)}`;
        }

        const response = await fetch(url);
        
        if (!response.ok) {
          throw new Error('Помилка завантаження даних');
        }

        const data = await response.json();
        setBooks(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Невідома помилка');
      } finally {
        setLoading(false);
      }
    };

    fetchBooks();
  }, [queryParams]);  

  const enrichedBooks = useMemo(() => {
    return books.map(book => {
      const author = authors.find(a => a.author_id === book.author_id);
      const publisher = publishers.find(p => p.publisher_id === book.publisher_id);
      
      return {
        ...book,
        author_name: author?.author_name || book.author_name || 'Невідомий автор',
        publisher_name: publisher?.name || book.publisher_name || 'Невідоме видавництво'
      };
    });
  }, [books, authors, publishers]);

  const booksForGrid = useMemo(() => {
    return enrichedBooks.map(book => ({
      book_id: book.book_id,
      book_title: book.book_title,
      author_name: book.author_name,
      book_price: book.book_price?.toString() || '0',
      book_cover_path: book.book_cover_path || 'default-book.jpg',
      book_quantity: book.book_quantity ?? 0,
    }));
  }, [enrichedBooks]);

  if (loading) {
    return (
      <CatalogContainer>
        <ContentContainer>
          <LoadingState>Завантаження каталогу...</LoadingState>
        </ContentContainer>
      </CatalogContainer>
    );
  }

  if (error) {
    return (
      <CatalogContainer>
        <ContentContainer>
          <EmptyState>
            <EmptyStateTitle>Помилка завантаження</EmptyStateTitle>
            <EmptyStateText>{error}</EmptyStateText>
          </EmptyState>
        </ContentContainer>
      </CatalogContainer>
    );
  }

  return (
    <CatalogContainer>
      <ContentContainer>
        <TopSection>
          <CategoryHead title={queryInfo.label}/>
        </TopSection>

        {books.length === 0 ? (
          <EmptyState>
            <EmptyStateTitle>Книги не знайдено</EmptyStateTitle>
            <EmptyStateText>
              {queryInfo.type === 'search' 
                ? `За запитом "${queryInfo.value}" нічого не знайдено`
                : `У цій категорії поки немає книг`
              }
            </EmptyStateText>
          </EmptyState>
        ) : (
          <BooksGrid books={booksForGrid} />
        )}
      </ContentContainer>
    </CatalogContainer>
  );
});

Catalog.displayName = 'Catalog';
export default Catalog;